import Flex from '@/components/common/Flex';
import SubtleBadge from '@/components/common/SubtleBadge';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import { getColor } from '@/helpers/utils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { hasRedirectTarget } from '../utils/backlinkCellFlags';

const decodeUrl = (value: string | null | undefined): string => {
  if (!value) return '';

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const renderUrlText = (
  url: string,
  boldDomain: boolean,
  hostName?: string | null
) => {
  const isHttps = url.startsWith('https://');
  const isHttp = url.startsWith('http://');
  const scheme = isHttps ? 'https' : isHttp ? 'http' : null;
  const displayUrl = scheme ? url.replace(`${scheme}://`, '') : url;

  if (!boldDomain || !scheme) return displayUrl;

  let domain = hostName?.trim() || '';
  if (!domain) {
    try {
      domain = new URL(url).host;
    } catch {
      return displayUrl;
    }
  }

  const domainStart = displayUrl.toLowerCase().indexOf(domain.toLowerCase());
  if (domainStart < 0) return displayUrl;

  return (
    <>
      {displayUrl.slice(0, domainStart)}
      <strong className="backlink-url-cell-domain">
        {displayUrl.slice(domainStart, domainStart + domain.length)}
      </strong>
      {displayUrl.slice(domainStart + domain.length)}
    </>
  );
};

const BacklinkUrlLine = ({
  url,
  boldDomain = false,
  hostName,
  breakAll = false
}: Record<string, any>) => {
  const decoded = decodeUrl(url);
  if (!decoded || decoded === 'null') return null;

  const isHttps = decoded.startsWith('https://');
  const isHttp = decoded.startsWith('http://');

  return (
    <div className="backlink-url-cell-main" title={decoded}>
      <a
        href={decoded}
        target="_blank"
        rel="noreferrer"
        className={`backlink-url-cell-link${
          breakAll ? ' backlink-url-cell-link-break-all' : ''
        }`}
      >
        {(isHttps || isHttp) && (
          <FontAwesomeIcon
            size="sm"
            icon={isHttps ? 'lock' : 'lock-open'}
            title={
              isHttps
                ? 'این آدرس HTTPS است'
                : 'این آدرس HTTP است و HTTPS نیست'
            }
            color={getColor(isHttps ? 'success' : 'danger')}
            className="backlink-url-cell-lock"
          />
        )}
        <span className="backlink-url-cell-text">
          {renderUrlText(decoded, boldDomain, hostName)}
        </span>
      </a>
    </div>
  );
};

const normalizeRedirectHop = hop => {
  if (Array.isArray(hop)) {
    return {
      statusCode: hop[1],
      url: hop[2]
    };
  }
  return {
    statusCode: hop?.status_code ?? hop?.statusCode,
    url: hop?.url
  };
};

type BacklinkBadge = {
  label: string;
  show?: boolean;
  color?: string;
  tooltip?: string;
  compact?: boolean;
  attention?: boolean;
  icon?: IconProp;
};

const BacklinkUrlCell = ({
  url,
  redirectTarget = null,
  redirectStatusCode = null,
  indirectLinkPath = [],
  badges = [],
  boldDomain = false,
  hostName,
  breakAll = false
}: Record<string, any>) => {
  const showRedirectTarget = hasRedirectTarget(redirectTarget);
  const redirectHops = (Array.isArray(indirectLinkPath)
    ? indirectLinkPath
    : []
  )
    .map(normalizeRedirectHop)
    .filter(hop => hasRedirectTarget(hop.url));
  const visibleBadges = (badges as BacklinkBadge[]).filter(
    (badge: BacklinkBadge) => badge.show
  );

  return (
    <Flex direction="column" className="gap-1 h-100 w-100 py-1">
      <BacklinkUrlLine
        url={url}
        boldDomain={boldDomain}
        hostName={hostName}
        breakAll={breakAll}
      />
      {showRedirectTarget && (
        <div
          className="d-flex align-items-center gap-1 text-truncate fs--2"
          style={{
            direction: 'ltr',
            paddingLeft: 0,
            maxWidth: '100%',
            lineHeight: 1.5
          }}
          title={`${redirectStatusCode} → ${redirectTarget}`}
        >
          <span
            aria-hidden="true"
            style={{ color: getColor('gray-600'), flex: '0 0 auto' }}
          >
            ↳
          </span>
          <span
            className="d-inline-flex align-items-center gap-1"
            style={{ flex: '0 0 auto' }}
          >
            <span
              className="px-1 rounded text-900"
              style={{
                backgroundColor: getColor('warning'),
                fontSize: '0.7rem',
                lineHeight: 1.4
              }}
            >
              {redirectStatusCode}
            </span>
          </span>
          <URLRenderer
            url={redirectTarget}
            enableCopyAndRedirect={false}
            className="text-truncate"
            style={{ minWidth: 0 }}
          />
        </div>
      )}
      {redirectHops.length > 1 && (
        <div
          className="d-flex flex-column gap-1 text-truncate fs--2"
          style={{ direction: 'ltr', paddingLeft: '1rem', maxWidth: '100%' }}
          title={redirectHops
            .map(hop => `${hop.statusCode || ''} → ${hop.url}`)
            .join(' | ')}
        >
          <span style={{ color: getColor('gray-600') }}>مسیر ریدایرکت</span>
          {redirectHops.map((hop, index) => (
            <div
              key={`${hop.url}-${index}`}
              className="d-flex align-items-center gap-1 text-truncate"
            >
              <span
                className="px-1 rounded text-900"
                style={{
                  backgroundColor: getColor('warning'),
                  fontSize: '0.7rem',
                  lineHeight: 1.4
                }}
              >
                {hop.statusCode || '—'}
              </span>
              <URLRenderer
                url={hop.url}
                enableCopyAndRedirect={false}
                className="text-truncate"
                style={{ minWidth: 0 }}
              />
            </div>
          ))}
        </div>
      )}
      {visibleBadges.length > 0 && (
        <Flex
          direction="row"
          justifyContent="end"
          className="gap-1 flex-wrap w-100"
        >
          {visibleBadges.map(
            ({ label, color, tooltip, compact, attention, icon }) => (
              <OverlayTrigger
                key={label}
                placement="top"
                overlay={
                  <Tooltip id={`backlink-badge-${label}`}>{tooltip}</Tooltip>
                }
              >
                <span className="d-inline-block">
                  {compact ? (
                    <span
                      className="px-1 rounded text-900"
                      style={{
                        backgroundColor: getColor('gray-200'),
                        fontSize: '0.7rem',
                        lineHeight: 1.4
                      }}
                    >
                      {label}
                    </span>
                  ) : (
                    <SubtleBadge
                      bg={color}
                      className={
                        attention || icon ? 'backlink-badge-attention' : ''
                      }
                    >
                      {label}
                      {(attention || icon) && (
                        <FontAwesomeIcon
                          icon={icon || 'exclamation-triangle'}
                          className="backlink-badge-attention-icon"
                          aria-hidden="true"
                        />
                      )}
                    </SubtleBadge>
                  )}
                </span>
              </OverlayTrigger>
            )
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default BacklinkUrlCell;
